<template>
    <div class="upload-img-section">
        <div class="upload-card"
             @drop.prevent="loadImage($event)"
             @dragover.prevent>
            <img :src="image" class="hidden" @load="imageLoaded" @error="imageError">
            <template v-if="image && imageExists">
                <div class="img-buttons">
                    <a href="#" v-if="editable" @click="editImage(image)">
                        <inline-svg src="/assets/img/icons/scissors-cut-circle.svg"></inline-svg>
                    </a>
                    <a href="#" @click="removeImage(image)">
                        <inline-svg src="/assets/img/icons/trash-circle.svg"></inline-svg>
                    </a>
                </div>
                <img :src="image" class="img-fluid" @load="imageLoaded" @error="imageError">
            </template>
            <div v-else class="upload-card-content">
                <template>
                    <inline-svg src="/assets/img/icons/upload-icon.svg"></inline-svg>
                    <label class="upload-card-content" style="cursor: pointer;">
                        <input style="display: none" type="file" name="file" accept="image/*" @change="loadImage($event)"
                               :multiple="true" :disabled="loading.upload" ref="input"/>
                        <span class="upload-text">
                        <span class="upload-link">{{ upload_link_message ? upload_link_message : 'Click to upload image' }}</span>
<!--                            or drag and drop-->
                    </span>
                        <span class="upload-text">SVG, PNG, JPG (max. 800x400px)</span>
                    </label>
                </template>
            </div>
        </div>
        <span v-if="hint">💡 Tip: {{this.hint}}</span>
        <modal name="cropImageModal" height="auto" width="650px" :scrollable="true">
            <div class="modal-content">
                <div class="modal-header d-flex flex-1 align-items-center justify-content-start gap-2">
                    <inline-svg src="/assets/img/icons/scissors-cut.svg"></inline-svg>
                    <div class="d-flex flex-column">
                        <h4 class="modal-title">Crop image</h4>
                        <div class="modal-subtitle-gray text-center">Upload a 800 x 400px image for best results.</div>
                    </div>
                </div>
                <div class="modal-body d-flex flex-column align-items-center pt-3 pb-4" :class="{'loading': loading.image}">
                    <div class="p-1" style="height: 400px; width: 600px">
                        <cropper ref="cropper"
                                 :stencil-props="{
                                    aspectRatio: 2,
                                    }"
                                 check-orientation
                                 default-boundaries="fill"
                                 backgroundClass="background"
                                 image-restriction="none"
                                 :src="uploadedImage" />
                    </div>
                </div>
                <div class="divider --top-12"></div>
                <div class="modal-footer">
                    <btn class="btn-outline-light btn-lg" @click="reset">Cancel</btn>
                    <btn class="btn-primary btn-lg" :loading="loading.upload" @click="uploadImage">Save</btn>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import {mapState} from 'vuex';
import draggable from 'vuedraggable';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { saveAs } from 'file-saver';

// This function is used to detect the actual image type,
function getMimeType(file, fallback = null) {
    const byteArray = new Uint8Array(file).subarray(0, 4);
    let header = '';
    for (let i = 0; i < byteArray.length; i++) {
        header += byteArray[i].toString(16);
    }
    switch (header) {
        case '89504e47':
            return 'image/png';
        case '47494638':
            return 'image/gif';
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
        case 'ffd8ffe3':
        case 'ffd8ffe8':
            return 'image/jpeg';
        default:
            return fallback;
    }
}

export default {
    name: 'ImageUploader',
    props: ['value', 'target', 'target_id', 'editable', 'hint', 'image_type', 'card_deck_id', 'card_id', 'upload_link_message'],
    components: {draggable, Cropper},
    computed: {
        ...mapState({
            languages: (state) => state.languages,
        }),
    },
    data() {
        return {
            loading: {
                image: false,
                upload: false,
                save: false,
                remove: false,
            },
            id: '',
            uploadedImage: null,
            image: '',
            edited: false,
            imageExists: true,
        };
    },
    mounted() {
        this.id = this._uid;
        this.image = this.value
    },
    watch: {
        value(a) {
            this.image = a;
        },
        image(image) {
            this.$emit('input', image);
        }
    },
    methods: {
        crop() {
            const { canvas } = this.$refs.cropper.getResult();
            canvas.toBlob((blob) => {
                saveAs(blob);
            }, this.uploadedImage);
        },
        reset() {
            this.$modal.hide('cropImageModal')
            this.uploadedImage = null;
        },
        loadImage(event) {
            this.loading.image = true;
            // Reference to the DOM input element
            const { files } = event.target;
            // Ensure that you have a file before attempting to read it
            if (files && files[0]) {
                // 1. Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
                if (this.uploadedImage) {
                    URL.revokeObjectURL(this.uploadedImage);
                }
                // 2. Create the blob link to the file to optimize performance:
                const blob = URL.createObjectURL(files[0]);
                // Create a new FileReader to read this image binary data
                const reader = new FileReader();
                // Define a callback function to run, when FileReader finishes its job
                reader.onload = (e) => {
                    // Note: arrow function used here, so that "this.image" refers to the image of Vue component
                    this.uploadedImage = blob;
                    this.loading.image = false;
                };
                reader.onerror = (e) => {
                    this.loading.image = false;
                }
                // Start the reader job - read file as a data url (base64 format)
                reader.readAsArrayBuffer(files[0]);
                this.$modal.show('cropImageModal')
            }
        },
        destroyed() {
            // Revoke the object URL, to allow the garbage collector to destroy the uploaded before file
            if (this.uploadedImage) {
                URL.revokeObjectURL(this.uploadedImage);
            }
        },
        uploadImage(e) {
            this.loading.upload = true;
            const { canvas } = this.$refs.cropper.getResult();
            if (canvas) {
                const formData = new FormData();
                canvas.toBlob((blob) => {
                    formData.append('file', blob);
                    if (this.image_type !== null) {
                        formData.append('type', this.image_type);
                    }
                    formData.append('old_image', this.image);
                    this.$http.post('/upload-file', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        emulateJSON: true,
                            files: true,
                    })
                        .then((res) => {
                            this.loading.upload = false;
                            this.$modal.hide('cropImageModal');
                            this.uploadedImage = null;
                            this.image = res.body;
                            this.edited = false;
                            this.$forceUpdate();
                    })
                        .catch((error) => {
                            this.edited = false;
                            this.loading.upload = false;
                        });
                }, 'image/jpeg');
            }
        },
        removeImage(image) {
            const self = this;
            this.$dialog.confirm('Are you sure you want to delete the image?', {
                okText: 'Yes',
                cancelText: 'No',
            })
                .then(() => {
                    self.loading.remove = true;
                    self.$http.post('/delete-file', {filePath: image, card_deck_id: this.card_deck_id, card_id: this.card_id})
                        .then(function (res) {
                            self.image = null;
                            self.uploadedImage = null;
                            self.loading.remove = false;
                            self.$forceUpdate();
                        })
                        .catch((res) => {
                            self.loading.remove = false;
                        });
                });
            this.$forceUpdate();
        },
        editImage(image) {
            this.edited = true;
            this.uploadedImage = image;
            this.$modal.show('cropImageModal');
        },
        imageLoaded() {
            this.imageExists = true;
        },
        imageError() {
            this.imageExists = false;
        },
    },
};
</script>
